import React, { useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  DateInput,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import { FormToolbar } from '../../common/FormToolbar';
import { PurchaseItemsGrid } from './PurchaseItemsGrid';

export const PurchaseCreate = (props: any) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [items, setItems] = useState<{ productId: number; code?: string; qty: number }[]>([]);

  const padNumber = (n: number, length: number = 5): string => {
    return n.toString().padStart(length, '0');
  };

  const downloadCsv = (poNo: string, poDate: string | undefined) => {
    const username = localStorage.getItem('username') || '';
    const dateObj = poDate ? new Date(poDate) : new Date();
    const dateStr = dateObj.toLocaleDateString('en-GB');

    const header = ['Username', 'DocSerialNo', 'Date', 'Itcd', 'Qty'];
    const rows = items
      .filter((it) => it.qty > 0)
      .map((it) => [
        username,
        poNo,
        dateStr,
        it.code || '',
        it.qty.toString(),
      ]);

    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${(v || '').replace(/"/g, '""')}"`).join(','))
      .join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${poNo || 'document'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (values: any) => {
    try {
      const toCreate = items.filter((it) => it.qty > 0);
      if (toCreate.length === 0) {
        notify('Please scan at least one item with quantity before saving', { type: 'warning' });
        return;
      }

      let poNo: string | undefined = values.poNo;
      const poDateIso = values.poDate
        ? new Date(values.poDate).toISOString()
        : new Date().toISOString();

      // first create header, allowing backend to assign ID
      const created: any = await dataProvider.create('purs', {
        data: {
          ...values,
          poDate: poDateIso,
          poNo: poNo, // may be undefined initially
        },
      });

      const purId = created.data.id;

      // if poNo not provided, derive it from the generated ID to ensure uniqueness
      if (!poNo && purId != null) {
        poNo = padNumber(purId);
        await dataProvider.update('purs', {
          id: purId,
          data: { ...created.data, poNo },
          previousData: created.data,
        });
      }
      for (const it of toCreate) {
        await dataProvider.create('pur-items', {
          data: {
            purId,
            productId: it.productId,
            qty: it.qty,
          },
        });
      }

      downloadCsv(poNo!, poDateIso);

      notify('Purchase created', { type: 'info' });
      redirect('/purs');
    } catch (e) {
      console.error(e);
      const anyErr: any = e;
      notify(anyErr?.message || 'Error creating purchase', { type: 'warning' });
    }
  };

  return (
    <React.Fragment>
      <Create title="Create Purchase" {...props}>
        <SimpleForm
          warnWhenUnsavedChanges
          toolbar={<FormToolbar onSave={handleSubmit} onClose={props.onClose} onCancel={props.onCancel} {...props} />}
          variant="outlined"
          save={handleSubmit}
        >
          <TextInput source="poNo" label="Doc. Serial No." fullWidth disabled />
          <DateInput source="poDate" label="Doc. Date" fullWidth defaultValue={new Date().toISOString()}/>

          

          <PurchaseItemsGrid onChange={setItems} />
        </SimpleForm>
      </Create>
    </React.Fragment>
  );
};
